import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './DateField.module.css';

type DateFieldType = 'date' | 'time' | 'datetime-local' | 'month';

const ICONS: Record<DateFieldType, string> = {
  date: '🗓️',
  time: '🕐',
  'datetime-local': '🗓️',
  month: '🗓️',
};

const PLACEHOLDER_KEYS: Record<DateFieldType, string> = {
  date: 'common.datePlaceholder',
  time: 'common.timePlaceholder',
  'datetime-local': 'common.dateTimePlaceholder',
  month: 'common.monthPlaceholder',
};

/**
 * A date/time input that looks the same on a phone as it does on a desktop.
 *
 * Left to itself, `<input type="date">` (and its `time`/`datetime-local`/
 * `month` siblings, which behave the same way) is one of the least
 * consistent controls the platform offers, and all three of its failure
 * modes showed up together on a real phone:
 *
 * - **iOS styles it as a native pill** with its own background and height,
 *   ignoring the app's own input styling — it reads as a stray grey button
 *   sitting in a form of otherwise-matching fields.
 * - **An empty one renders completely blank** there: no `dd/mm/yyyy` hint,
 *   nothing to say it's a date field at all.
 * - **Android Chrome draws no calendar glyph**, unlike desktop Chrome, so
 *   the usual visual cue for "tap this to pick a date" is simply absent.
 *
 * So the native chrome is stripped (`DateField.module.css`) and the three
 * missing pieces — app-matching box, placeholder, calendar icon — are drawn
 * here instead. The input itself is still a real native input, so tapping it
 * opens whatever picker the platform has; only its appearance is ours.
 */
export default function DateField({
  id,
  type,
  value,
  onChange,
  ariaLabel,
}: {
  id?: string;
  type: DateFieldType;
  value: string;
  onChange: (value: string) => void;
  ariaLabel?: string;
}) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  // Firefox draws no picker indicator at all, so a click there lands on the
  // text and nothing opens. `showPicker` covers that case; it throws where
  // unsupported (iOS Safari) or outside a user gesture, and those platforms
  // open their own picker from the hidden-but-present indicator instead.
  const openPicker = () => {
    try {
      inputRef.current?.showPicker();
    } catch {
      /* Not supported here — the native tap behaviour still applies. */
    }
  };

  return (
    <div className={styles.wrap}>
      <input
        ref={inputRef}
        id={id}
        className={`input ${styles.field}`}
        type={type}
        value={value}
        aria-label={ariaLabel}
        onClick={openPicker}
        onChange={(e) => onChange(e.target.value)}
      />
      {value ? null : <span className={styles.placeholder}>{t(PLACEHOLDER_KEYS[type])}</span>}
      <span className={styles.icon} aria-hidden="true">
        {ICONS[type]}
      </span>
    </div>
  );
}
